import { AppNotification } from '@digital-twins/shared-types'
import { useTranslation } from 'react-i18next'
import { useRouter } from '@/routing/navigation'
import { getNotificationHref } from '@/lib/notifications/get-notification-href'
import {
  formatNotificationDisplay,
  formatNotificationRelativeTime,
} from '@/lib/notifications/format-notification-display'
import { cn } from '@/lib/utils'

function getAnnouncementImageUrl(notification: AppNotification): string | null {
  if (notification.type !== 'cs_announcement') {
    return null
  }

  const imageUrl = notification.metadata?.imageUrl
  return typeof imageUrl === 'string' && imageUrl.trim() ? imageUrl.trim() : null
}

function getAnnouncementVideoUrl(notification: AppNotification): string | null {
  if (notification.type !== 'cs_announcement') {
    return null
  }

  const videoUrl = notification.metadata?.videoUrl
  return typeof videoUrl === 'string' && videoUrl.trim() ? videoUrl.trim() : null
}

interface NotificationItemProps {
  notification: AppNotification
  isUnread: boolean
  className?: string
}

export function NotificationItem({ notification, isUnread, className }: NotificationItemProps) {
  const router = useRouter()
  const { t, i18n } = useTranslation('app')
  const copy = formatNotificationDisplay(notification, t)
  const imageUrl = getAnnouncementImageUrl(notification)
  const videoUrl = getAnnouncementVideoUrl(notification)
  const announcementBody = copy.body
  const isPhotoAnnouncement = Boolean(imageUrl) || Boolean(videoUrl)
  const targetHref = getNotificationHref(notification)

  function handleClick() {
    if (targetHref) {
      router.push(targetHref)
    }
  }

  return (
    <button
      type="button"
      data-testid="notification-item"
      onClick={handleClick}
      className={cn(
        'group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
        targetHref
          ? 'cursor-pointer hover:bg-muted/50 dark:hover:bg-muted'
          : 'cursor-default',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'whitespace-normal break-words text-sm text-foreground',
            isUnread ? 'font-medium' : 'font-normal',
          )}
        >
          {copy.title}
        </p>

        {announcementBody && (
          <p className="mt-1 whitespace-normal break-words text-sm font-normal text-muted-foreground">
            {announcementBody}
          </p>
        )}

        {videoUrl && (
          <video
            src={videoUrl}
            controls
            className="mt-2.5 w-full rounded-md"
            style={{ maxHeight: 160 }}
          />
        )}

        {imageUrl && !videoUrl && (
          <img
            src={imageUrl}
            alt=""
            className="mt-2.5 h-36 w-full rounded-md object-cover"
          />
        )}

        <p className={cn('text-xs text-muted-foreground', imageUrl || videoUrl || announcementBody ? 'mt-2' : 'mt-0.5')}>
          {formatNotificationRelativeTime(notification.createdAt, t, i18n.language)}
        </p>
      </div>

      {isUnread && (
        <div
          aria-hidden="true"
          className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"
        />
      )}
    </button>
  )
}
